/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Checkbox,
  useToast,
  Box,
  Icon,
  Spinner,
  StackDivider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  FormHelperText,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import api from "utils/api";

const VehicleRow = memo(function VehicleRow({ arac, checked, onToggle }) {
  const pid = arac.id;
  const plaka = (arac.plaka || "").trim() || "—";
  const imei = (arac.imei || "").trim() || String(pid);
  const line = `${plaka} (${imei})`;
  return (
    <HStack
      role="button"
      tabIndex={0}
      cursor="pointer"
      px={2}
      py={2}
      spacing={3}
      borderRadius="sm"
      bg={checked ? "blue.50" : "transparent"}
      _hover={{ bg: checked ? "blue.50" : "gray.50" }}
      onClick={() => onToggle(pid)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(pid);
        }
      }}
    >
      <Checkbox
        isChecked={checked}
        isReadOnly
        pointerEvents="none"
        colorScheme="blue"
        size="sm"
      />
      <Text fontSize="sm" color="gray.800" noOfLines={2} flex={1} minW={0}>
        {line}
      </Text>
    </HStack>
  );
});

const SearchResultRow = memo(function SearchResultRow({ arac, onToggle }) {
  const plaka = (arac.plaka || "").trim() || "—";
  const imei = (arac.imei || "").trim() || String(arac.id);
  return (
    <HStack
      role="button"
      tabIndex={0}
      px={3}
      py={2}
      cursor="pointer"
      justify="space-between"
      _hover={{ bg: "gray.50" }}
      onClick={() => onToggle(arac.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(arac.id);
        }
      }}
    >
      <Text fontSize="sm" color="gray.800" noOfLines={1}>
        {plaka} ({imei})
      </Text>
    </HStack>
  );
});

function EkHesapDuzenleModal({
  isOpen,
  onClose,
  ekHesapId,
  firmaId,
  firmaAdi,
  onSaved,
  onDeleted,
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [detail, setDetail] = useState(null);
  const [allAraclar, setAllAraclar] = useState([]);
  const [aracFilter, setAracFilter] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [canCreateSub, setCanCreateSub] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const cancelDeleteRef = useRef(null);
  const [editEmail, setEditEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const normalizedFilter = aracFilter.trim().toLowerCase();
  const filteredAraclar = useMemo(() => {
    if (!normalizedFilter) return allAraclar;
    return allAraclar.filter((a) => {
      const p = (a.plaka || "").toLowerCase();
      const i = (a.imei || "").toLowerCase();
      return p.includes(normalizedFilter) || i.includes(normalizedFilter);
    });
  }, [allAraclar, normalizedFilter]);

  const selectedAraclar = useMemo(() => {
    if (!allAraclar.length || !selectedIds.length) return [];
    return allAraclar.filter((a) => selectedSet.has(a.id));
  }, [allAraclar, selectedIds.length, selectedSet]);

  const visibleAraclar = useMemo(() => {
    // Alt liste bağımsız olarak sadece görüntülenen (seçili) araçları gösterir.
    return selectedAraclar;
  }, [selectedAraclar]);

  const searchResults = useMemo(() => {
    if (!isSearchFocused) return [];
    const source = normalizedFilter ? filteredAraclar : allAraclar;
    return source.filter((a) => !selectedSet.has(a.id)).slice(0, 100);
  }, [
    allAraclar,
    filteredAraclar,
    isSearchFocused,
    normalizedFilter,
    selectedSet,
  ]);

  const loadAll = useCallback(async () => {
    if (!ekHesapId || !firmaId) return;
    setInitialLoad(true);
    try {
      const [dRes, devRes] = await Promise.all([
        api.get(`/api/firmalar/ek-hesaplar/kayit/${ekHesapId}`),
        api.get(`/api/devices?firma_id=${firmaId}`),
      ]);
      const dJson = await dRes.json();
      const devJson = await devRes.json();
      if (!dRes.ok) {
        throw new Error(dJson.detail || "Alt hesap yüklenemedi");
      }
      setDetail(dJson);
      setCanCreateSub(dJson.userLimit !== -1);
      const ids = Array.isArray(dJson.secilen_arac_ids) ? dJson.secilen_arac_ids.map(Number) : [];
      setSelectedIds(ids);
      setAllAraclar(devRes.ok && Array.isArray(devJson) ? devJson : []);
    } catch (e) {
      toast({
        title: "Hata",
        description: e.message || "Veri alınamadı",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      onClose?.();
    } finally {
      setInitialLoad(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose yalnızca hata durumunda
  }, [ekHesapId, firmaId, toast]);

  useEffect(() => {
    if (!isOpen || !ekHesapId) {
      setDetail(null);
      setAllAraclar([]);
      setAracFilter("");
      setIsSearchFocused(false);
      setSelectedIds([]);
      setEditEmail("");
      setNewPassword("");
      return;
    }
    loadAll();
  }, [isOpen, ekHesapId, loadAll]);

  useEffect(() => {
    if (detail?.email != null) {
      setEditEmail(detail.email);
    }
  }, [detail]);

  const toggleArac = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return [...next];
    });
  }, []);

  const handleSave = async () => {
    const emailTrim = editEmail.trim();
    if (!emailTrim) {
      toast({
        title: "E-posta gerekli",
        description: "Geçerli bir e-posta girin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        userLimit: canCreateSub ? 50 : -1,
        secilen_arac_ids: selectedIds,
        email: emailTrim,
      };
      const pwdTrim = newPassword.trim();
      if (pwdTrim) payload.password = pwdTrim;
      const res = await api.put(`/api/firmalar/ek-hesaplar/kayit/${ekHesapId}`, payload);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Güncellenemedi");
      }
      toast({
        title: "Kaydedildi",
        description: data.message || "Alt hesap güncellendi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setNewPassword("");
      onSaved?.();
      onClose();
    } catch (e) {
      toast({
        title: "Hata",
        description: e.message || "İşlem başarısız",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleteConfirmOpen(false);
    setLoading(true);
    try {
      const res = await api.delete(`/api/firmalar/ek-hesaplar/kayit/${ekHesapId}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Alt hesap silinemedi");
      }
      toast({
        title: "Silindi",
        description: data.message || "Alt hesap silindi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onDeleted?.();
      onClose();
    } catch (e) {
      toast({
        title: "Hata",
        description: e.message || "Silme işlemi başarısız",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const title = detail?.ek_hesap_adi || "Alt hesap";

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      motionPreset="none"
      scrollBehavior="inside"
      closeOnOverlayClick={!loading}
    >
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="xl" maxH="88vh" display="flex" flexDirection="column" mx={3}>
        <ModalCloseButton size="sm" isDisabled={loading} />
        <Box px={5} pt={5} pb={3} borderBottomWidth="1px" borderColor="gray.100">
          <Text fontSize="md" fontWeight="semibold">
            Alt hesabı düzenle
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {title}
            {firmaAdi ? ` · ${firmaAdi}` : ""}
          </Text>
        </Box>

        <ModalBody flex="1" overflowY="auto" px={5} py={4}>
          {initialLoad ? (
            <HStack py={12} justify="center">
              <Spinner color="blue.500" />
            </HStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Box
                px={3}
                py={2.5}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
              >
                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>
                  Giriş bilgileri (Traccar)
                </Text>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm" mb={1}>
                    E-posta
                  </FormLabel>
                  <Input
                    size="sm"
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>
                    Yeni şifre
                  </FormLabel>
                  <Input
                    size="sm"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Değiştirmek için yazın"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <FormHelperText fontSize="xs" mt={1}>
                    Boş bırakırsanız şifre değişmez.
                  </FormHelperText>
                </FormControl>
              </Box>

              <Box
                px={3}
                py={2.5}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="gray.50"
              >
                <Checkbox
                  isChecked={canCreateSub}
                  onChange={(e) => setCanCreateSub(e.target.checked)}
                  colorScheme="blue"
                  size="md"
                >
                  <Text as="span" fontSize="sm" fontWeight="600" color="gray.800">
                    Kullanıcı oluşturabilir
                  </Text>
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={1} ml={7}>
                  İşaretliyse alt hesap Traccar tarafında alt kullanıcı ekleyebilir.
                </Text>
              </Box>

              <Box
                position="relative"
                borderWidth="1px"
                borderColor="blue.400"
                borderRadius="md"
                bg="white"
                px={3}
                pt={4}
                pb={2}
              >
                <Text
                  position="absolute"
                  top="-10px"
                  left={3}
                  bg="white"
                  px={1.5}
                  fontSize="xs"
                  fontWeight="600"
                  color="blue.600"
                  letterSpacing="0.02em"
                >
                  Araçlar
                </Text>
                <Box position="relative" mb={2}>
                  <HStack spacing={2}>
                    <Input
                      size="sm"
                      placeholder="Plaka veya IMEI ara..."
                      value={aracFilter}
                      onChange={(e) => setAracFilter(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => {
                        setTimeout(() => setIsSearchFocused(false), 120);
                      }}
                      autoComplete="off"
                      bg="white"
                    />
                    <Icon as={FiSearch} color="gray.400" boxSize={5} flexShrink={0} />
                  </HStack>
                  {isSearchFocused ? (
                    <Box
                      position="absolute"
                      left={0}
                      right={10}
                      top="calc(100% + 6px)"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="white"
                      zIndex={20}
                      boxShadow="sm"
                      maxH="200px"
                      overflowY="auto"
                    >
                      <VStack
                        align="stretch"
                        spacing={0}
                        divider={<StackDivider borderColor="gray.100" />}
                      >
                        {searchResults.length === 0 ? (
                          <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>
                            {selectedIds.length > 0
                              ? "Seçili olmayan araç bulunamadı."
                              : "Aramaya uygun araç yok."}
                          </Text>
                        ) : (
                          searchResults.slice(0, 5).map((arac) => (
                            <SearchResultRow
                              key={`search-${arac.id}`}
                              arac={arac}
                              onToggle={toggleArac}
                            />
                          ))
                        )}
                      </VStack>
                    </Box>
                  ) : null}
                </Box>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {normalizedFilter
                    ? `${filteredAraclar.length} / ${allAraclar.length} araç · ${selectedIds.length} seçili`
                    : `${selectedIds.length} seçili`}
                </Text>

                <Text fontSize="xs" color="gray.600" mb={1} fontWeight="600">
                  Görüntülenen araçlar
                </Text>
                {allAraclar.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                    Firmada araç yok.
                  </Text>
                ) : (
                  <VStack
                    align="stretch"
                    spacing={0}
                    maxH="240px"
                    overflowY="auto"
                    pr={1}
                    divider={<StackDivider borderColor="gray.100" />}
                  >
                    {visibleAraclar.length === 0 ? (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={4}
                      >
                        Henüz görüntülenen araç seçilmedi.
                      </Text>
                    ) : (
                      visibleAraclar.map((arac) => (
                        <VehicleRow
                          key={arac.id}
                          arac={arac}
                          checked={selectedSet.has(arac.id)}
                          onToggle={toggleArac}
                        />
                      ))
                    )}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.100" py={3} px={5}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            mr={2}
            onClick={() => setIsDeleteConfirmOpen(true)}
            isDisabled={initialLoad || loading}
          >
            Alt hesabı sil
          </Button>
          <Button variant="ghost" size="sm" mr="auto" onClick={onClose} isDisabled={loading}>
            Kapat
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleSave}
            isLoading={loading}
            isDisabled={initialLoad}
          >
            Kaydet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    <AlertDialog
      isOpen={isDeleteConfirmOpen}
      leastDestructiveRef={cancelDeleteRef}
      onClose={() => setIsDeleteConfirmOpen(false)}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent maxW="460px" borderRadius="lg">
          <AlertDialogHeader fontSize="md" fontWeight="700" pb={2}>
            Alt hesap silinsin mi?
          </AlertDialogHeader>
          <AlertDialogBody fontSize="sm" color="gray.700" pb={4}>
            Bu işlem geri alınamaz. Alt hesap sistemden kaldırılacaktır.
          </AlertDialogBody>
          <AlertDialogFooter pt={0}>
            <Button
              ref={cancelDeleteRef}
              size="sm"
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              Vazgeç
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              ml={2}
              onClick={handleDelete}
              isLoading={loading}
            >
              Sil
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
}

export default EkHesapDuzenleModal;
